import React, { useCallback, useState } from 'react';
import { ArrowDownTrayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { TeamRegistrationService } from '../../../TeamRegistration/TeamRegistrationService';

type Props = {
  disabled?: boolean;
  /** Exports this one Jahresmeldung as a compact link, in either tab. */
  reportId?: number;
};

/**
 * "Meldungen als Excel herunterladen" of the association panel: hands over the
 * workbook of the still open Meldungen - one sheet per club and one per
 * Wettbewerb - or of a single Jahresmeldung when reportId is given.
 */
const ReportExcelDownload: React.FC<Props> = ({ disabled, reportId }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = useCallback(async () => {
    if (isDownloading) return;
    try {
      setError(null);
      setIsDownloading(true);
      await TeamRegistrationService.downloadAssociationReportExcel(reportId);
    } catch (err) {
      console.error('Excel export error:', err);
      setError(
        'Die Excel-Datei konnte nicht erstellt werden. Bitte versuchen Sie es erneut.',
      );
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading, reportId]);

  if (reportId !== undefined) {
    return (
      <div className="flex flex-col items-end gap-1">
        <button
          type="button"
          onClick={handleDownload}
          disabled={disabled || isDownloading}
          title="Diese Jahresmeldung als Excel herunterladen"
          className="inline-flex items-center gap-1 font-medium text-blue-500 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isDownloading ? (
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
          ) : (
            <ArrowDownTrayIcon className="h-4 w-4" />
          )}
          Excel
        </button>
        {error && <div className="text-xs text-red-500">{error}</div>}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={handleDownload}
        disabled={disabled || isDownloading}
        title="Meldungen als Excel herunterladen"
        className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-2 text-xs font-bold text-white shadow-sm transition hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isDownloading ? (
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
        ) : (
          <ArrowDownTrayIcon className="h-4 w-4" />
        )}
        {isDownloading
          ? 'Excel wird erstellt ...'
          : 'Meldungen als Excel herunterladen'}
      </button>
      {error && <div className="text-xs text-red-500">{error}</div>}
    </div>
  );
};

export default ReportExcelDownload;
